import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { Bar, defaults as ChartDefaults, Pie } from 'react-chartjs-2';
import { useDispatch } from 'react-redux';
import { HasClassName } from '../../component-utils';
import { orderBy } from '../../data-utils';
import { formatDuration } from '../../date-utils';
import { useSelector } from '../../hook-utils';
import { AgentStatistic, GosGraphType, QueueIconEnum, QueueStats, StatisticCallType, UserLevel } from '../../model';
import { actions } from '../../store';
import { selectConfigProp, selectStatistics } from '../../store/reducers/config';
import { getNow } from '../../store/reducers/now';
import { selectAllGosLimits, selectGosLimits, selectFavQueues } from '../../store/reducers/queues';
import Button from '../Button';
import { ReactComponent as SettingsIcon } from '../icons/toolbar-settings.svg';
import Modal from '../Modal';
import Speedo from '../SpeedoChart';
import { TabButton, TabsHeader } from '../Tabs';
import Timer from '../Timer';
import './Dashboard.scss';
import { ChartTooltipItem, ChartData } from 'chart.js';
import { selectAgentStatusTab } from '../../store/reducers/localSettings';

ChartDefaults.global.maintainAspectRatio = false
ChartDefaults.global.responsive = true
ChartDefaults.global.animation = {
  ...(ChartDefaults.global.animation || {}),
  duration: 0,
}

interface Props extends HasClassName {
}

const Tabs = ['Agent', 'Favourite queues']
export default function Dashboard({ className }: Props) {
  const dispatch = useDispatch()
  const tab = useSelector(selectAgentStatusTab)
  const stats = useSelector(selectStatistics)

  return <div className={classNames('Dashboard', className)}>
    <TabsHeader className='tabsHeader'>
      {Tabs.map((s, i) => <TabButton key={i} active={i === tab} onClick={() => dispatch(actions.setAgentStatusTab(i))}>{s}</TabButton>)}
    </TabsHeader>
    <div className='tabContent'>
      {tab === 0 && <AgentCharts stats={stats} />}
      {tab === 1 && <Favourites />}
    </div>
  </div>
}

function ChartCard({ title, children, className }: React.PropsWithChildren<{ title: React.ReactNode }> & HasClassName) {
  return <div className={classNames('ChartCard', className)}>
    <h2>{title}</h2>
    <div className='chartContainer'>
      {React.Children.only(children)}
    </div>
  </div>
}

type AgentChartProps = {
  stats: AgentStatistic | null
}

export function AgentCharts({ stats }: AgentChartProps) {
  return <>
    <ChartCard title='Agent ASA'>
      <AgentAsaChart stats={stats} />
    </ChartCard>
    <ChartCard title='Queue Time'>
      <QueueTimeChart stats={stats} />
    </ChartCard>
    <ChartCard title='Productivity'>
      <ProductivityChart stats={stats} />
    </ChartCard>
    <ChartCard title='Call Stats'>
      <CallStatsChart stats={stats} />
    </ChartCard>
    <ChartCard title='Agent Pullbacks'>
      <AgentAsa2Chart stats={stats} />
    </ChartCard>
  </>
}

function AgentAsaChart({ stats }: AgentChartProps) {
  const queueStats = getCallStats(stats, StatisticCallType.Queue)
  if (!queueStats) { return null }
  const value = queueStats.answeredCallCount ? queueStats.ringTime / queueStats.answeredCallCount : 0

  return <Speedo value={value} breaks={[5, 8, 10]} />
}

function QueueTimeChart({ stats }: AgentChartProps) {
  const queueStats = getCallStats(stats, StatisticCallType.Queue)
  const custom = stats && stats.custom
  const custom7 = custom && custom.find(c => c.key === "7")

  if (!queueStats || !custom7) { return null }

  return <Pie legend={{ position: 'right' }} data={{
    labels: ['Idle', 'Ringing', 'Talk', 'Hold', 'Wrap'],
    datasets: [{
      data: [custom7.value, queueStats.ringTime, queueStats.talkTime, queueStats.holdTime, queueStats.wrapTime],
      backgroundColor: ['#0070c0', '#ff0000', '#00b050', '#7030a0', '#636363'],
    }],
  }} options={{
    tooltips: {
      callbacks: {
        label: FormatSecondsLabel,
      },
    },
  }}
  />
}

function ProductivityChart({ stats }: AgentChartProps) {
  const custom = stats && stats.custom
  const custom1 = custom && custom.find(c => c.key === "1")
  const custom2 = custom && custom.find(c => c.key === "2")
  const custom5 = custom && custom.find(c => c.key === "5")
  const custom6 = custom && custom.find(c => c.key === "6")
  const custom7 = custom && custom.find(c => c.key === "7")
  const custom8 = custom && custom.find(c => c.key === "8")

  if (!custom1 || !custom2 || !custom5 || !custom6 || !custom7 || !custom8) { return null }

  return <Pie legend={{ position: 'right' }} data={{
    labels: ['Break', 'Office', 'Idle', 'Call', 'Wrap'],
    datasets: [{
      data: [custom5.value, custom6.value, custom7.value, custom8.value, custom1.value + custom2.value],
      backgroundColor: ['#7030a0', '#ffff00', '#0070C0', '#00b050', '#636363'],
    }],
  }} options={{
    tooltips: {
      callbacks: {
        label: FormatSecondsLabel,
      },
    },
  }} />
}

function FormatSecondsLabel(tooltipItem: ChartTooltipItem, chartData: ChartData) {
  if (!chartData || !chartData.datasets || tooltipItem.datasetIndex === undefined || !chartData.datasets[tooltipItem.datasetIndex].data || !chartData.labels || tooltipItem.index === undefined) return ''
  const label = chartData.labels[tooltipItem.index];
  const dataset = chartData.datasets[tooltipItem.datasetIndex]

  if (!dataset.data) return ''

  const data = dataset.data[tooltipItem.index]
  if (!label || typeof data !== 'number') return ''

  return label + ": " + formatDuration(data, true)
}

function FormatLabel(tooltipItem: ChartTooltipItem, chartData: ChartData) {
  if (!chartData || !chartData.datasets || tooltipItem.datasetIndex === undefined || !chartData.datasets[tooltipItem.datasetIndex].data || !chartData.labels || tooltipItem.index === undefined) return ''
  const label = chartData.labels[tooltipItem.index];
  const dataset = chartData.datasets[tooltipItem.datasetIndex]

  if (!dataset.data) return ''

  const data = dataset.data[tooltipItem.index]
  if (!label || typeof data !== 'number') return ''

  return label + ": " + data
}

function CallStatsChart({ stats }: AgentChartProps) {
  if (!stats) { return null }

  const averageHandleTime = stats.call.map(c => c.answeredCallCount ? (c.talkTime + c.holdTime + c.wrapTime) / c.answeredCallCount : 0)
  let averageHandleTimeMax = 5
  for (const average of averageHandleTime) {
    if (average > averageHandleTimeMax) {
      averageHandleTimeMax = average
    }
  }

  if (averageHandleTimeMax > 5) {
    averageHandleTimeMax += Math.floor(averageHandleTimeMax * .25)
  }

  const longCalls = stats.call.map(c => c.longCalls)
  let longCallsMax = 5
  for (const average of longCalls) {
    if (average > longCallsMax) {
      longCallsMax = average
    }
  }
  if (longCallsMax > 5) {
    longCallsMax += Math.floor(longCallsMax * .25)
  }

  const step = Math.floor(averageHandleTimeMax / 5)

  return <Bar options={{
    scales: {
      yAxes: [{
        id: 'y-axis-2',
        gridLines: { display: false },
        ticks: {
          stepSize: step,
          beginAtZero: true,
          min: 0,
          max: averageHandleTimeMax,
          callback: (value: any, index: any, values: any) => {
            return formatDuration(parseInt(value), true)
          },
        },
        position: 'right',
      }, {
        id: 'y-axis-1',
        ticks: {
          stepSize: 1,
          beginAtZero: true,
          min: 0,
          max: longCallsMax,
        },
      }],
    },
    tooltips: {
      callbacks: {
        label: (tooltipItem: ChartTooltipItem, chartData: ChartData) => {
          if (tooltipItem.datasetIndex === 0) {
            return FormatLabel(tooltipItem, chartData)
          }
          return FormatSecondsLabel(tooltipItem, chartData)
        },
      },
    },
  }} data={{
    labels: stats.call.map(c => StatisticCallType[c.type]),
    datasets: [{
      label: 'Long calls',
      data: longCalls,
      yAxisID: 'y-axis-1',
      type: 'line',
      borderColor: '#ed7d31',
      pointBackgroundColor: '#ed7d31',
    }, {
      label: 'Average handle time',
      data: averageHandleTime,
      yAxisID: 'y-axis-2',
      type: 'bar',
      backgroundColor: '#0070C0',
      fill: false,
    }],
  }} width={null!} />
}

function AgentAsa2Chart({ stats }: AgentChartProps) {
  const queueStats = getCallStats(stats, StatisticCallType.Queue)
  if (!stats || !queueStats) { return null }

  const value = queueStats.answeredCallCount ? stats.pullBacks / queueStats.answeredCallCount : 0

  return <Speedo value={value * 100} breaks={[5, 10, 20]} />
}

function getCallStats(stats: AgentStatistic | null, type: StatisticCallType) {
  if (!stats) { return null }
  return stats.call.filter(c => c.type === type)[0] || null
}

function Favourites() {
  const queues = useSelector(selectFavQueues)

  return <div className='Favourites'>
    {[...queues].sort(orderBy(q => q.id)).map(q => <QueueCharts key={q.id} queue={q} />)}
  </div>
}

export function QueueCharts({ queue }: { queue: QueueStats }) {
  const depth = queue.agents ? queue.count / queue.agents : 0
  const limits = useSelector(s => selectAllGosLimits(s, queue.id))

  const now = useSelector(getNow)
  const time = (queue.time || 0) && (now - (queue.time || 0)) / 1000

  const formatTime = useCallback((v: number) => formatDuration(v * 60), [])

  function getStatus() {
    switch (queue.icon) {
      case QueueIconEnum.OpenAlert: return 'Open Alert'
      case QueueIconEnum.OpenWarning: return 'Open Warning'
      case QueueIconEnum.NoAgents: return 'No agents'
      default: return QueueIconEnum[queue.icon]
    }
  }

  function getStatusColor() {
    switch (queue.icon) {
      case QueueIconEnum.OpenAlert:
        return 'red'

      case QueueIconEnum.OpenWarning:
      case QueueIconEnum.NoAgents:
        return 'yellow'
    }
  }

  const total = queue.abandoned + queue.answer
  const colour = Math.max(...limits.map(({ type, yellow, red }) => {
    if (type === GosGraphType.Gos) {
      if (queue.agentStatus <= red) { return 2 }
      if (queue.agentStatus <= yellow) { return 1 }
      return 0
    }

    let value: number
    switch (type) {
      case GosGraphType.Depth:
        value = depth
        break

      case GosGraphType.Time:
        value = time
        break

      default:
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const _exhaustiveCheck: never = type
        value = type
    }

    if (value > red) { return 2 }
    if (value > yellow) { return 1 }
    return 0
  }))

  return <div className='QueueCharts'>
    <div className='stats'>
      <header className={classNames({
        red: colour === 2,
        yellow: colour === 1,
      })}>{queue.id} {queue.description}</header>
      <table>
        <tbody>
          <tr>
            <td>Status</td>
            <td className={getStatusColor()}>
              {getStatus()}
            </td>
            <td />
          </tr>
          <tr>
            <td>Queuing</td>
            <td>{queue.count}</td>
            <td>
              {queue.time ? <Timer ts={queue.time} /> : '00:00'}
            </td>
          </tr>
          <tr>
            <td>Agents</td>
            <td>{queue.agents}</td>
            <td />
          </tr>
          <tr>
            <td>Depth</td>
            <td>{depth.toFixed()}</td>
            <td />
          </tr>
          <tr>
            <td>Answered</td>
            <td>{queue.answer}</td>
            <td>
              {formatDuration(total ? queue.ansQTimeTotal / total : 0)}
            </td>
          </tr>
          <tr>
            <td>Abandoned</td>
            <td>{(total ? queue.abandoned * 100 / total : 0).toFixed()}%</td>
            <td>
              {formatDuration(total ? queue.abnQTimeTotal / total : 0)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <GosChart queueId={queue.id} type={GosGraphType.Gos} title='Service level' value={queue.agentStatus} />
    <GosChart queueId={queue.id} type={GosGraphType.Depth} title='Queue depth' value={depth} />
    <GosChart queueId={queue.id} type={GosGraphType.Time} title='Queue time' value={time} format={formatTime} />
  </div>
}

type GosChartProps = {
  queueId: string
  type: GosGraphType
  title: string
  value: number
  format?: (value: number) => string
}

function GosChart({ queueId, type, title, value, format }: GosChartProps) {
  let limits = useSelector(s => selectGosLimits(s, { queueId, type }))
  if (!limits) { return null }

  if (type === GosGraphType.Time) {
    limits = [limits[0] / 60, limits[1] / 60, limits[2] / 60]
    value /= 60
  }

  if (type === GosGraphType.Gos) {
    limits = [limits[2], limits[1], limits[0]] as const
  }

  return <>
    <ChartCard title={<div className='title'>
      {title}
      <ThresholdSettings className='settingsButton' queueId={queueId} type={type} />
    </div>}>
      <Speedo value={value} breaks={limits} reverse={type === GosGraphType.Gos} format={format} />
    </ChartCard>
  </>
}

function ThresholdSettings({ queueId, type, className }: { queueId: string, type: GosGraphType } & HasClassName) {
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  let limits = useSelector(s => selectGosLimits(s, { queueId, type }))
  const [values, setValues] = useState<string[]>([])
  const userLevel = useSelector(s => selectConfigProp(s, 'baseUserLevel'))

  const factor = type === GosGraphType.Time ? 60 : 1

  useEffect(() => {
    if (!limits) { return }
    setValues(limits.map(x => (x / factor).toString()))
  }, [factor, limits])

  function updateValue(value: string, index: number) {
    const newVal = [...values]
    newVal[index] = value
    setValues(newVal)
  }

  function onSave(e: React.FormEvent) {
    e.preventDefault()
    dispatch(actions.updateGosLimits(queueId, type, values.map(x => parseFloat(x) * factor)))
    setOpen(false)
  }

  if (!userLevel || userLevel < UserLevel.Supervisor || userLevel > UserLevel.IPFXTechnician) { return null }

  function renderSection(index: number, name: string) {
    const prev = type === GosGraphType.Gos
      ? index === 2 ? null : parseFloat(values[index + 1])
      : index === 0 ? null : parseFloat(values[index - 1])

    const ctlrs = [
      <div className={classNames('prev', type !== GosGraphType.Gos && 'right-align')}>
        {prev === null ? 0 : Number.isNaN(prev) ? null : prev + 1}
      </div>,
      <> &ndash; </>,
      <input required type='number' value={values[index]} onChange={e => updateValue(e.currentTarget.value, index)} />,
    ]

    if (type === GosGraphType.Gos) { ctlrs.reverse() }

    return <label key={index}>
      <div>{name}</div>
      <div children={ctlrs} />
    </label>
  }

  return <>
    <button className={className} onClick={() => setOpen(true)} title='Adjust thresholds'>
      <SettingsIcon />
    </button>
    <Modal isOpen={open} onRequestClose={() => setOpen(false)}>
      <h2>Chart thresholds</h2>
      <form className='ThresholdSettings' onSubmit={onSave}>
        <div>
          {renderSection(0, 'Green')}
          {renderSection(1, 'Yellow')}
          {renderSection(2, 'Red')}
        </div>
        <div>
          <Button primary type='submit'>OK</Button>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
        </div>
      </form>
    </Modal>
  </>
}
